import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Clock, Settings, CreditCard, Calendar, CheckCircle2, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";

interface Activity {
  id: string;
  name: string;
  start_time?: string;
  end_time?: string;
  requires_day_selection: boolean;
}

interface ActivityConfig {
  id: string;
  name: string;
  billingType: 'per_session' | 'monthly';
  selectedDays: number[];
  requiresDaySelection: boolean;
}

interface ActivityConfigModalProps {
  activity: Activity | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: ActivityConfig) => void;
}

const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

export function ActivityConfigModal({ activity, isOpen, onClose, onConfirm }: ActivityConfigModalProps) {
  const [billingType, setBillingType] = useState<'per_session' | 'monthly'>('monthly');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  if (!activity) return null;

  const handleConfirm = () => {
    // Verificar si requiere selección de días y es tipo "por día" y no se han seleccionado
    if (activity.requires_day_selection && billingType === 'per_session' && selectedDays.length === 0) {
      toast({
        title: "Días requeridos",
        description: "Debes seleccionar al menos un día para esta actividad",
        variant: "destructive",
      });
      return;
    }

    const config: ActivityConfig = {
      id: activity.id,
      name: activity.name,
      billingType,
      selectedDays,
      requiresDaySelection: activity.requires_day_selection,
    };

    onConfirm(config);
    
    // Reset state
    setBillingType('monthly');
    setSelectedDays([]);
    onClose();
  };

  const handleClose = () => {
    setBillingType('monthly');
    setSelectedDays([]);
    onClose();
  };

  const toggleDay = (dayValue: number) => {
    if (selectedDays.includes(dayValue)) {
      setSelectedDays(prev => prev.filter(d => d !== dayValue));
    } else {
      setSelectedDays(prev => [...prev, dayValue]);
    }
  };

  const ConfigContent = () => (
    <div className="space-y-10">
      {/* Activity Header */}
      <div className="text-center space-y-4 sm:space-y-6 py-2 sm:py-4">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-3xl shadow-xl">
          <Settings className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-sm" />
        </div>
        <div className="space-y-2 sm:space-y-3">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight px-4">{activity.name}</h3>
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full text-sm font-medium text-gray-700 shadow-sm">
            <Clock className="w-4 h-4" />
            {activity.start_time && activity.end_time 
              ? `${activity.start_time.slice(0, 5)} - ${activity.end_time.slice(0, 5)}`
              : 'Horario por confirmar'
            }
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <label className="text-xl font-bold text-gray-900 tracking-tight">Uso del Servicio</label>
        </div>
        <div className="grid gap-4">
          {/* Monthly Card */}
          <Card 
            className={`cursor-pointer transition-all duration-300 border-2 hover:shadow-lg ${
              billingType === 'monthly'
                ? 'border-green-500 bg-gradient-to-br from-green-50 to-green-100 shadow-md transform scale-[1.02]'
                : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
            }`}
            onClick={() => setBillingType('monthly')}
          >
            <CardContent className="p-5">
              <div className="flex items-center space-x-4">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  billingType === 'monthly'
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300'
                }`}>
                  {billingType === 'monthly' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full transition-all ${
                      billingType === 'monthly' ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                    <h4 className="font-semibold text-gray-900">Uso mensual</h4>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 ml-6">
                    Pago fijo mensual independiente de la asistencia
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  billingType === 'monthly'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <CreditCard className="w-4 h-4" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Per Session Card */}
          <Card 
            className={`cursor-pointer transition-all duration-300 border-2 hover:shadow-lg ${
              billingType === 'per_session'
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md transform scale-[1.02]'
                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
            }`}
            onClick={() => setBillingType('per_session')}
          >
            <CardContent className="p-5">
              <div className="flex items-center space-x-4">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  billingType === 'per_session'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {billingType === 'per_session' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full transition-all ${
                      billingType === 'per_session' ? 'bg-blue-500' : 'bg-gray-400'
                    }`} />
                    <h4 className="font-semibold text-gray-900">Uso días sueltos</h4>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 ml-6">
                    Pago individual por cada día de asistencia
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  billingType === 'per_session'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <Calendar className="w-4 h-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Day Selection */}
      {activity.requires_day_selection && billingType === 'per_session' && (
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <label className="text-xl font-bold text-gray-900 tracking-tight">
              Días de Asistencia 
              <span className="text-red-500 ml-2 text-lg">*</span>
            </label>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-200 shadow-sm">
            <p className="text-sm font-medium text-blue-900 mb-5">
              Selecciona los días de la semana en los que el estudiante asistirá a esta actividad
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
              {dayNames.map((day, index) => {
                // Map array index to day values: Lunes=1, Martes=2, Miércoles=3, Jueves=4, Viernes=5
                const dayValue = index + 1;
                const isSelected = selectedDays.includes(dayValue);
                return (
                  <Button
                    key={`day-${index}`}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    className={`
                      w-full h-11 sm:h-12 px-2 sm:px-3
                      text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl 
                      transition-all duration-200 
                      touch-manipulation select-none
                      ${isSelected 
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg ring-2 ring-blue-200 text-white" 
                        : "border-gray-200 hover:border-blue-300 hover:bg-blue-50 active:bg-blue-100 text-gray-700"
                      }
                    `}
                    onClick={() => toggleDay(dayValue)}
                  >
                    <div className="flex items-center justify-center gap-1 w-full">
                      {isSelected && <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />}
                      <span className="leading-none truncate text-center">
                        {day.slice(0, 3)}
                      </span>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
          
          {selectedDays.length === 0 && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-xl border border-red-200">
              <X className="w-4 h-4 text-red-500" />
              <p className="text-sm font-medium text-red-700">Debes seleccionar al menos un día para continuar</p>
            </div>
          )}
          
          {selectedDays.length > 0 && (
            <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-xl border border-green-200">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <p className="text-sm font-medium text-green-700">
                {selectedDays.length} día{selectedDays.length > 1 ? 's' : ''} seleccionado{selectedDays.length > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 sm:gap-4 pt-6 sm:pt-8">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          className="flex-1 h-12 sm:h-14 rounded-2xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 hover:shadow-md text-sm sm:text-base"
        >
          <X className="mr-1 sm:mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Cancelar</span>
          <span className="sm:hidden">Cancelar</span>
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          className="flex-1 h-12 sm:h-14 rounded-2xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 font-semibold text-sm sm:text-base"
          disabled={activity.requires_day_selection && billingType === 'per_session' && selectedDays.length === 0}
        >
          <CheckCircle2 className="mr-1 sm:mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Confirmar Selección</span>
          <span className="sm:hidden">Confirmar</span>
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DrawerContent className="max-h-[95vh] rounded-t-3xl bg-gradient-to-br from-white to-gray-50">
          <DrawerHeader className="text-center pb-4">
            <DrawerTitle className="text-2xl font-bold text-gray-900 tracking-tight">Configurar Actividad</DrawerTitle>
            <DrawerDescription className="text-gray-600 text-base">
              Personaliza los detalles de inscripción para esta actividad
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-6 pb-8 overflow-y-auto max-h-[calc(95vh-120px)]">
            <ConfigContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto rounded-3xl border-0 shadow-2xl bg-gradient-to-br from-white to-gray-50">
        <DialogHeader className="text-center pb-4">
          <DialogTitle className="text-2xl font-bold text-gray-900 tracking-tight">Configurar Actividad</DialogTitle>
          <DialogDescription className="text-gray-600 text-base">
            Personaliza los detalles de inscripción para esta actividad
          </DialogDescription>
        </DialogHeader>
        <ConfigContent />
      </DialogContent>
    </Dialog>
  );
}